import React, { useState, useEffect, useCallback, useRef } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Image, DownloadSimple, ArrowsClockwise, Sparkle, Eye,
  CaretRight, CaretDown, Clock, Trash, CheckCircle,
  Rows, SquaresFour, ArrowRight, Lightning, ShareFat, PencilSimple, CursorClick
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';
import { EarlyAccessBanner } from '../components/EarlyAccessBanner';
import { shareOrDownload, downloadFile, shareContent } from '../utils/share';
import { useUpgrade } from '../components/UpgradeSystem';
import { pixelFlyerGenerated } from '../utils/pixel';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/* ═══ Template Metadata ═══ */
const TEMPLATE_META = {
  product_launch: {
    icon: '01',
    color: '#c8f060',
    description: 'Announce your new product, feature, or service with a premium dark layout.',
    fieldLabels: {
      headline: 'Headline',
      subheadline: 'Subheadline',
      body_text: 'Body Text',
      cta_text: 'Call to Action',
      brand_name: 'Brand Name',
    },
    placeholders: {
      headline: 'Introducing something new',
      subheadline: 'The future of your industry starts here',
      body_text: 'Built for people who demand more.',
      cta_text: 'Learn More',
      brand_name: 'Your Brand',
    },
  },
  limited_offer: {
    icon: '02',
    color: '#f59e0b',
    description: 'Create urgency with a bold discount or flash sale visual.',
    fieldLabels: {
      headline: 'Sale Tag',
      offer_text: 'Offer Amount',
      discount_code: 'Discount Code',
      cta_text: 'Call to Action',
      brand_name: 'Brand Name',
      expiry_text: 'Expiry Text',
    },
    placeholders: {
      headline: 'Flash Sale',
      offer_text: '50% OFF',
      discount_code: 'SAVE50',
      cta_text: 'Shop Now',
      brand_name: 'Your Brand',
      expiry_text: 'Ends midnight Sunday',
    },
  },
  brand_quote: {
    icon: '03',
    color: '#a78bfa',
    description: 'Share a powerful quote from your founder, team, or customer.',
    fieldLabels: {
      quote_text: 'Quote',
      author_name: 'Author Name',
      author_title: 'Author Title',
      brand_name: 'Brand Name',
    },
    placeholders: {
      quote_text: 'The best way to predict the future is to create it.',
      author_name: 'Your Name',
      author_title: 'Founder & CEO',
      brand_name: 'Your Brand',
    },
  },
  new_arrival: {
    icon: '04',
    color: '#22d3ee',
    description: 'Spotlight a new product, feature, or collection drop.',
    fieldLabels: {
      headline: 'Badge Text',
      feature_text: 'Feature Name',
      description: 'Description',
      cta_text: 'Call to Action',
      brand_name: 'Brand Name',
    },
    placeholders: {
      headline: 'Just Dropped',
      feature_text: 'The All-New Collection',
      description: 'Designed for those who don\'t settle.',
      cta_text: 'Explore Now',
      brand_name: 'Your Brand',
    },
  },
  event_promo: {
    icon: '05',
    color: '#ec4899',
    description: 'Promote an event, webinar, workshop, or live session.',
    fieldLabels: {
      event_name: 'Event Name',
      date_text: 'Date',
      time_text: 'Time',
      description: 'Description',
      cta_text: 'Call to Action',
      brand_name: 'Brand Name',
      location_text: 'Location',
    },
    placeholders: {
      event_name: 'Design Systems Summit',
      date_text: 'March 28, 2026',
      time_text: '7:00 PM CET',
      description: 'Join industry leaders for actionable insights.',
      cta_text: 'Register Free',
      brand_name: 'Your Brand',
      location_text: 'Online',
    },
  },
  social_proof: {
    icon: '06',
    color: '#38bdf8',
    description: 'Showcase impressive stats and metrics to build trust.',
    fieldLabels: {
      headline: 'Headline',
      stat1_value: 'Stat 1 Value',
      stat1_label: 'Stat 1 Label',
      stat2_value: 'Stat 2 Value',
      stat2_label: 'Stat 2 Label',
      stat3_value: 'Stat 3 Value',
      stat3_label: 'Stat 3 Label',
      cta_text: 'Call to Action',
      brand_name: 'Brand Name',
    },
    placeholders: {
      headline: 'Trusted by Thousands',
      stat1_value: '10K+',
      stat1_label: 'Active Users',
      stat2_value: '98%',
      stat2_label: 'Satisfaction',
      stat3_value: '4.9',
      stat3_label: 'Star Rating',
      cta_text: 'Join Them',
      brand_name: 'Your Brand',
    },
  },
  coming_soon: {
    icon: '07',
    color: '#818cf8',
    description: 'Build anticipation for an upcoming launch or product reveal.',
    fieldLabels: {
      headline: 'Headline',
      teaser_text: 'Teaser Text',
      launch_date: 'Launch Date',
      cta_text: 'Call to Action',
      brand_name: 'Brand Name',
    },
    placeholders: {
      headline: 'Something Big is Coming',
      teaser_text: 'We\'ve been working on something that will change the way you think about design.',
      launch_date: 'April 2026',
      cta_text: 'Get Notified',
      brand_name: 'Your Brand',
    },
  },
  testimonial: {
    icon: '08',
    color: '#f472b6',
    description: 'Feature a customer review with star rating and social proof.',
    fieldLabels: {
      quote_text: 'Customer Quote',
      customer_name: 'Customer Name',
      customer_role: 'Customer Role',
      rating: 'Star Rating (1-5)',
      brand_name: 'Brand Name',
    },
    placeholders: {
      quote_text: 'This product completely transformed our workflow. I can\'t imagine going back.',
      customer_name: 'Sarah Chen',
      customer_role: 'Head of Marketing, TechCorp',
      rating: '5',
      brand_name: 'Your Brand',
    },
  },
  comparison: {
    icon: '09',
    color: '#10b981',
    description: 'Compare before vs after, or your solution against alternatives.',
    fieldLabels: {
      headline: 'Headline',
      before_title: 'Left Column Title',
      before_items: 'Left Items (one per line)',
      after_title: 'Right Column Title',
      after_items: 'Right Items (one per line)',
      cta_text: 'Call to Action',
      brand_name: 'Brand Name',
    },
    placeholders: {
      headline: 'Why Choose Us?',
      before_title: 'Before',
      before_items: 'Manual processes\nSlow turnaround\nInconsistent quality',
      after_title: 'After',
      after_items: 'Fully automated\nInstant delivery\nPremium every time',
      cta_text: 'Make the Switch',
      brand_name: 'Your Brand',
    },
  },
  tips_card: {
    icon: '10',
    color: '#fb923c',
    description: 'Share quick tips, advice, or a mini how-to guide.',
    fieldLabels: {
      headline: 'Headline',
      tip1: 'Tip 1',
      tip2: 'Tip 2',
      tip3: 'Tip 3',
      cta_text: 'Call to Action',
      brand_name: 'Brand Name',
    },
    placeholders: {
      headline: '3 Tips to Level Up',
      tip1: 'Start with a clear strategy before creating content.',
      tip2: 'Consistency beats perfection. Post regularly.',
      tip3: 'Engage with your audience in the first 30 minutes.',
      cta_text: 'Save for Later',
      brand_name: 'Your Brand',
    },
  },
  countdown_sale: {
    icon: '11',
    color: '#ef4444',
    description: 'Create urgency with a countdown-style flash sale graphic.',
    fieldLabels: {
      headline: 'Sale Tag',
      discount_percent: 'Discount Percentage',
      sale_end_text: 'Ends When',
      cta_text: 'Call to Action',
      brand_name: 'Brand Name',
    },
    placeholders: {
      headline: 'FLASH SALE',
      discount_percent: '50',
      sale_end_text: 'Ends tonight at midnight',
      cta_text: 'Shop Now',
      brand_name: 'Your Brand',
    },
  },
  team_spotlight: {
    icon: '12',
    color: '#8b5cf6',
    description: 'Highlight a team member or founder with their quote.',
    fieldLabels: {
      person_name: 'Person Name',
      person_title: 'Title / Role',
      quote_text: 'Quote',
      brand_name: 'Brand Name',
    },
    placeholders: {
      person_name: 'Jane Doe',
      person_title: 'Co-Founder & CEO',
      quote_text: 'Building something meaningful takes patience, craft, and a refusal to settle.',
      brand_name: 'Your Brand',
    },
  },
  feature_grid: {
    icon: '13',
    color: '#06b6d4',
    description: 'Showcase 4 features or benefits in a clean grid layout.',
    fieldLabels: {
      headline: 'Headline',
      feat1_title: 'Feature 1 Title',
      feat1_desc: 'Feature 1 Description',
      feat2_title: 'Feature 2 Title',
      feat2_desc: 'Feature 2 Description',
      feat3_title: 'Feature 3 Title',
      feat3_desc: 'Feature 3 Description',
      feat4_title: 'Feature 4 Title',
      feat4_desc: 'Feature 4 Description',
      brand_name: 'Brand Name',
    },
    placeholders: {
      headline: 'Everything you need',
      feat1_title: 'Fast Setup',
      feat1_desc: 'Get started in under 2 minutes',
      feat2_title: 'On Brand',
      feat2_desc: 'Smart automation for your workflow',
      feat3_title: 'Analytics',
      feat3_desc: 'Track every metric that matters',
      feat4_title: 'Integrations',
      feat4_desc: 'Connect with 100+ tools',
      brand_name: 'Your Brand',
    },
  },
  milestone: {
    icon: '14',
    color: '#eab308',
    description: 'Celebrate a milestone or achievement with bold metrics.',
    fieldLabels: {
      headline: 'Badge Text',
      metric_value: 'Big Number',
      metric_label: 'Metric Label',
      description: 'Description',
      cta_text: 'Call to Action',
      brand_name: 'Brand Name',
    },
    placeholders: {
      headline: 'We just hit a milestone',
      metric_value: '10,000',
      metric_label: 'happy customers',
      description: 'Thank you for believing in us from day one.',
      cta_text: 'Join the community',
      brand_name: 'Your Brand',
    },
  },
  poll_engagement: {
    icon: '15',
    color: '#14b8a6',
    description: 'Drive engagement with a this-or-that poll or question card.',
    fieldLabels: {
      question_text: 'Question',
      option_a: 'Option A',
      option_b: 'Option B',
      cta_text: 'Call to Action',
      brand_name: 'Brand Name',
    },
    placeholders: {
      question_text: 'Which one are you?',
      option_a: 'Early bird',
      option_b: 'Night owl',
      cta_text: 'Vote now in stories',
      brand_name: 'Your Brand',
    },
  },
};

/* ═══ Zustand Store ═══ */
const useTemplateStore = create(
  persist(
    (set) => ({
      selectedTemplate: null,
      fields: {},
      format: 'square',
      rendering: false,
      result: null,
      history: [],
      setField: (k, v) => set({ [k]: v }),
      setFields: (f) => set({ fields: f }),
      updateField: (key, val) => set(state => ({ fields: { ...state.fields, [key]: val } })),
      reset: () => set({ selectedTemplate: null, fields: {}, format: 'square', result: null }),
    }),
    {
      name: 'scalitos-template-studio',
      partialize: (s) => ({
        selectedTemplate: s.selectedTemplate,
        fields: s.fields,
        format: s.format,
        history: s.history,
      }),
    }
  )
);

/* ═══ Template Card ═══ */
const TemplateCard = ({ id, template, meta, selected, onSelect }) => {
  const isActive = selected === id;
  return (
    <motion.button
      data-testid={`template-card-${id}`}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onSelect(id)}
      className="text-left p-5 rounded-xl transition-all w-full"
      style={{
        background: isActive ? `${meta.color}08` : '#141414',
        border: `1px solid ${isActive ? `${meta.color}40` : 'rgba(255,255,255,0.06)'}`,
      }}
    >
      <div className="flex items-start justify-between mb-3">
        <span className="text-[11px] font-bold px-2.5 py-1 rounded-md" style={{ background: `${meta.color}15`, color: meta.color }}>{meta.icon}</span>
        {isActive && <CheckCircle size={16} weight="fill" style={{ color: meta.color }} />}
      </div>
      <p className="text-sm font-semibold mb-1" style={{ color: '#f5f0e8' }}>{template.name}</p>
      <p className="text-[11px] leading-relaxed" style={{ color: 'rgba(245,240,232,0.4)' }}>{meta.description}</p>
    </motion.button>
  );
};

/* ═══ Main Page ═══ */
export default function TemplateStudioPage() {
  const store = useTemplateStore();
  const { handleApiError } = useUpgrade();
  const [templates, setTemplates] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [aiDescription, setAiDescription] = useState('');
  const [ideaLoading, setIdeaLoading] = useState(false);
  const [bgGenerating, setBgGenerating] = useState(false);
  const [bgStyle, setBgStyle] = useState('creative');
  const iframeRef = useRef(null);
  const previewContainerRef = useRef(null);
  const [previewScale, setPreviewScale] = useState(0.45);
  const [previewHtml, setPreviewHtml] = useState('');
  const [flyerMode, setFlyerMode] = useState('template');
  const [aiStyle, setAiStyle] = useState('modern');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiFields, setAiFields] = useState({ headline: '', subheadline: '', body_text: '', cta_text: '', brand_name: '' });

  useEffect(() => {
    loadTemplates();
    loadHistory();
    // Read batch prefill from Marketing Coach
    try {
      const raw = sessionStorage.getItem('batch_prefill');
      if (raw) {
        const { type, data } = JSON.parse(raw);
        sessionStorage.removeItem('batch_prefill');
        if (type === 'ai_flyer' && data) {
          // Switch to Custom Design mode and prefill
          setDesignMode('ai');
          setAiFields({
            headline: data.headline || '',
            subheadline: data.subheadline || data.sub_headline || '',
            body_text: data.body_text || data.description || '',
            cta_text: data.cta_text || '',
            brand_name: data.brand_name || '',
          });
          toast.success('Flyer pre-filled from Marketing Coach!');
        } else if (type === 'flyer' && data) {
          const tplId = data.template_id || 'product_launch';
          const meta = TEMPLATE_META[tplId];
          if (meta) {
            const prefillFields = {};
            Object.keys(meta.placeholders).forEach(k => {
              prefillFields[k] = data[k] || '';
            });
            store.setField('selectedTemplate', tplId);
            store.setFields(prefillFields);
            store.setField('result', null);
            toast.success('Flyer pre-filled from Marketing Coach!');
          }
        }
      }
    } catch {}
  }, []);

  const loadTemplates = async () => {
    try {
      const { data } = await axios.get(`${API}/api/media/templates`);
      setTemplates(data.templates || []);
    } catch {}
  };

  const loadHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/api/media/history`);
      store.setField('history', data.renders || []);
    } catch {}
  };

  const selectTemplate = (id) => {
    const meta = TEMPLATE_META[id];
    if (!meta) return;
    const defaultFields = {};
    Object.keys(meta.placeholders).forEach(k => {
      defaultFields[k] = store.fields[k] || '';
    });
    store.setField('selectedTemplate', id);
    store.setFields(defaultFields);
    store.setField('result', null);
  };

  const renderTemplate = useCallback(async () => {
    if (!store.selectedTemplate) return;
    store.setField('rendering', true);
    store.setField('result', null);

    // Fill empty fields with placeholders
    const meta = TEMPLATE_META[store.selectedTemplate];
    const filledFields = { ...store.fields };
    Object.entries(meta.placeholders).forEach(([k, v]) => {
      if (!filledFields[k]) filledFields[k] = v;
    });

    try {
      const { data } = await axios.post(`${API}/api/media/render`, {
        template_id: store.selectedTemplate,
        format: store.format,
        fields: filledFields,
      });
      store.setField('result', data);
      store.setField('rendering', false);
      loadHistory();
      pixelFlyerGenerated('template');
    } catch (err) {
      store.setField('rendering', false);
      if (!handleApiError(err)) {
        console.error('Render failed:', err);
      }
    }
  }, [store.selectedTemplate, store.fields, store.format]);

  const downloadResult = () => {
    if (!store.result?.download_url) return;
    downloadFile(store.result.download_url, `flyer-${store.selectedTemplate || 'ai'}.png`);
  };

  const generateAiFlyer = async () => {
    if (!aiFields.headline.trim()) {
      toast.error('Enter a headline for your flyer');
      return;
    }
    setAiGenerating(true);
    store.setField('result', null);
    try {
      const { data } = await axios.post(`${API}/api/media/ai-flyer`, {
        ...aiFields,
        style: aiStyle,
        format: store.format,
      });
      store.setField('result', data);
      toast.success('Flyer ready.');
      loadHistory();
      pixelFlyerGenerated('ai');
    } catch (err) {
      if (!handleApiError(err)) {
        toast.error('Flyer build failed. Please try again.');
      }
    } finally {
      setAiGenerating(false);
    }
  };

  const deleteRender = async (renderId) => {
    try {
      await axios.delete(`${API}/api/media/history/${renderId}`);
      store.setField('history', store.history.filter(r => r.render_id !== renderId));
    } catch {}
  };

  const aiGenerateFields = async () => {
    if (!store.selectedTemplate) return;
    const meta = TEMPLATE_META[store.selectedTemplate];
    if (!meta) return;
    setAiGenerating(true);
    try {
      const { data } = await axios.post(`${API}/api/template/ai-generate`, {
        template_id: store.selectedTemplate,
        description: aiDescription || '',
        fields: Object.keys(meta.fieldLabels),
      });
      if (data.fields) {
        const newFields = {};
        Object.entries(data.fields).forEach(([k, v]) => {
          if (typeof v === 'string') newFields[k] = v;
        });
        store.setFields(newFields);
        toast.success('Copy ready. Edit fields or click Render.');
        setTimeout(() => fetchPreviewHtml(), 200);
      }
    } catch (err) {
      if (!handleApiError(err)) {
        toast.error('Generation failed. Try again.');
      }
    }
    setAiGenerating(false);
  };

  const selectedMeta = store.selectedTemplate ? TEMPLATE_META[store.selectedTemplate] : null;
  const selectedTpl = templates.find(t => t.id === store.selectedTemplate);

  // ═══ Live Preview: Fetch editable HTML ═══
  const fetchPreviewHtml = useCallback(async () => {
    if (!store.selectedTemplate) return;
    const meta = TEMPLATE_META[store.selectedTemplate];
    if (!meta) return;
    const filledFields = { ...store.fields };
    Object.entries(meta.placeholders).forEach(([k, v]) => {
      if (!filledFields[k]) filledFields[k] = v;
    });
    try {
      const { data } = await axios.post(`${API}/api/media/preview-html`, {
        template_id: store.selectedTemplate,
        fields: filledFields,
        format: store.format,
      });
      setPreviewHtml(data.html);
    } catch {}
  }, [store.selectedTemplate, store.format]);

  // Bidirectional field edit: panel → iframe
  const handleFieldEdit = (key, value) => {
    store.updateField(key, value);
    if (iframeRef.current?.contentDocument) {
      const el = iframeRef.current.contentDocument.querySelector(`[data-field="${key}"]`);
      if (el && el.textContent !== value) el.textContent = value;
    }
  };

  // Load preview on template/format change
  useEffect(() => {
    if (store.selectedTemplate) {
      fetchPreviewHtml();
    }
  }, [store.selectedTemplate, store.format, fetchPreviewHtml]);

  // Write HTML into iframe
  useEffect(() => {
    if (!previewHtml || !iframeRef.current) return;
    const iframe = iframeRef.current;
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;
    doc.open();
    doc.write(previewHtml);
    doc.close();
  }, [previewHtml]);

  // Listen for field edits from iframe (WYSIWYG)
  useEffect(() => {
    const handler = (event) => {
      if (event.data?.type === 'flyer-field-edit') {
        store.updateField(event.data.field, event.data.value);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // Resize observer for preview scaling
  useEffect(() => {
    const node = previewContainerRef.current;
    if (!node) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setPreviewScale(entry.contentRect.width / 1080);
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [store.selectedTemplate]);

  // Generate AI Background
  const generateBackground = async () => {
    setBgGenerating(true);
    try {
      // Build content context from the current fields
      const fieldValues = Object.values(store.fields).filter(v => v && typeof v === 'string');
      const contentContext = fieldValues.slice(0, 3).join(', ');

      const { data } = await axios.post(`${API}/api/media/template/ai-background`, {
        style: bgStyle,
        template_type: store.selectedTemplate,
        format: store.format,
        content_context: contentContext,
      });
      if (data.data_url) {
        store.setField('background_image', data.data_url);
        toast.success('Background ready. Click Render to apply.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Background generation failed');
    }
    setBgGenerating(false);
  };

  // Build Full Idea — mode-aware
  const generateFullIdea = async () => {
    setIdeaLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/generate-idea`, {
        type: 'flyer',
        product: aiDescription || Object.values(store.fields).filter(v => v).join(' '),
        description: aiDescription,
      });
      const idea = data.idea;
      const f = idea?.fields || idea || {};

      if (flyerMode === 'ai') {
        // Custom Design mode: populate flyer fields directly — robust field mapping
        const allVals = Object.values(f).filter(v => typeof v === 'string');
        setAiFields({
          headline: f.headline || f.heading || f.statement || f.event_name || allVals[0] || '',
          subheadline: f.subheadline || f.sub_headline || f.subtitle || f.offer_text || f.supporting_text || f.feature_text || allVals[1] || '',
          body_text: f.body_text || f.description || f.body || f.story_text || f.testimonial_text || f.quote || f.fun_fact || allVals[2] || '',
          cta_text: f.cta_text || f.cta || f.call_to_action || 'Get Started',
          brand_name: f.brand_name || f.brand || aiDescription?.split(' ')[0] || '',
        });
        toast.success('Idea ready for your flyer.');
      } else {
        // Template mode: try to select a matching template
        const tplId = idea?.template || 'product_launch';
        const meta = TEMPLATE_META[tplId];
        if (meta) {
          selectTemplate(tplId);
          const mappedFields = {};
          Object.keys(meta.placeholders).forEach(k => {
            mappedFields[k] = f[k] || '';
          });
          setTimeout(() => store.setFields(mappedFields), 100);
          toast.success('Idea ready. Review and render.');
          setTimeout(() => fetchPreviewHtml(), 200);
        } else {
          // Template not found — fall back to product_launch
          selectTemplate('product_launch');
          const fallbackFields = {
            headline: f.headline || '',
            subheadline: f.subheadline || '',
            body_text: f.body_text || f.description || '',
            cta_text: f.cta_text || 'Get Started',
            brand_name: f.brand_name || '',
          };
          setTimeout(() => store.setFields(fallbackFields), 100);
          toast.success('Idea ready.');
          setTimeout(() => fetchPreviewHtml(), 200);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate idea');
    }
    setIdeaLoading(false);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="template-studio-page">
        <EarlyAccessBanner />

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl lg:text-3xl font-semibold" style={{ color: '#f5f0e8', fontFamily: "'Instrument Serif', serif" }}>
              Template Studio
            </h1>
            <p className="text-sm mt-1" style={{ color: 'rgba(245,240,232,0.35)' }}>
              Create professional flyers and social graphics in seconds. 15 premium templates, 0 design skills needed.
            </p>
          </div>
          {store.history.length > 0 && (
            <button
              data-testid="open-history-btn"
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(245,240,232,0.5)' }}
            >
              <Clock size={14} /> {store.history.length} rendered
            </button>
          )}
        </div>

        {/* Write this for me - Full */}
        <div className="flex items-center gap-3">
          <input
            data-testid="flyer-idea-input"
            value={aiDescription}
            onChange={e => setAiDescription(e.target.value)}
            placeholder="Describe your product or brand..."
            className="flex-1 px-3 py-2.5 rounded-lg text-xs"
            style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.08)', color: '#f5f0e8' }}
          />
          <button
            data-testid="ai-full-idea-btn"
            onClick={generateFullIdea}
            disabled={ideaLoading}
            className="px-4 py-2.5 rounded-lg text-xs font-medium flex items-center gap-2 whitespace-nowrap transition-all"
            style={{
              background: 'rgba(168,139,250,0.08)',
              border: '1px solid rgba(168,139,250,0.2)',
              color: '#a78bfa',
              opacity: ideaLoading ? 0.6 : 1,
            }}>
            {ideaLoading ? <ArrowsClockwise size={12} className="animate-spin" /> : <Sparkle size={12} weight="fill" />}
            {ideaLoading ? 'Generating...' : 'Write this for me'}
          </button>
        </div>

        {/* Mode Toggle */}
        <div className="flex items-center gap-2">
          <button
            data-testid="mode-template-btn"
            onClick={() => { setFlyerMode('template'); store.setField('result', null); }}
            className="px-4 py-2 rounded-lg text-xs font-semibold transition-all"
            style={{
              background: flyerMode === 'template' ? 'rgba(200,240,96,0.1)' : 'rgba(255,255,255,0.02)',
              border: `1px solid ${flyerMode === 'template' ? 'rgba(200,240,96,0.25)' : 'rgba(255,255,255,0.06)'}`,
              color: flyerMode === 'template' ? '#c8f060' : 'rgba(245,240,232,0.4)',
            }}>
            <Rows size={12} className="inline mr-1.5" /> Templates
          </button>
          <button
            data-testid="mode-ai-btn"
            onClick={() => { setFlyerMode('ai'); store.setField('result', null); }}
            className="px-4 py-2 rounded-lg text-xs font-semibold transition-all"
            style={{
              background: flyerMode === 'ai' ? 'rgba(168,139,250,0.1)' : 'rgba(255,255,255,0.02)',
              border: `1px solid ${flyerMode === 'ai' ? 'rgba(168,139,250,0.25)' : 'rgba(255,255,255,0.06)'}`,
              color: flyerMode === 'ai' ? '#a78bfa' : 'rgba(245,240,232,0.4)',
            }}>
            <Sparkle size={12} weight="fill" className="inline mr-1.5" /> Custom Design
          </button>
        </div>

        {/* Custom Design Mode */}
        {flyerMode === 'ai' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Flyer Inputs */}
            <div className="space-y-4 p-5 rounded-2xl" style={{ background: '#0c0c0c', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Sparkle size={14} weight="fill" style={{ color: '#a78bfa' }} />
                <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#a78bfa' }}>Custom Flyer Designer</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(168,139,250,0.1)', color: '#a78bfa' }}>3 credits</span>
              </div>
              <p className="text-[11px] mb-3" style={{ color: 'rgba(245,240,232,0.3)' }}>
                ScalitOS builds a complete flyer with graphics, illustrations, and professional typography.
              </p>

              <div className="space-y-3">
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-wider mb-1 block" style={{ color: 'rgba(245,240,232,0.3)' }}>Headline *</label>
                  <input data-testid="ai-flyer-headline" value={aiFields.headline} onChange={e => setAiFields(f => ({...f, headline: e.target.value}))}
                    placeholder="e.g. Summer Sale 50% Off" className="w-full px-3 py-2.5 rounded-lg text-sm"
                    style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.08)', color: '#f5f0e8' }} />
                </div>
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-wider mb-1 block" style={{ color: 'rgba(245,240,232,0.3)' }}>Subheadline</label>
                  <input data-testid="ai-flyer-subheadline" value={aiFields.subheadline} onChange={e => setAiFields(f => ({...f, subheadline: e.target.value}))}
                    placeholder="e.g. Limited time only" className="w-full px-3 py-2.5 rounded-lg text-sm"
                    style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.08)', color: '#f5f0e8' }} />
                </div>
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-wider mb-1 block" style={{ color: 'rgba(245,240,232,0.3)' }}>Body Text</label>
                  <textarea data-testid="ai-flyer-body" value={aiFields.body_text} onChange={e => setAiFields(f => ({...f, body_text: e.target.value}))}
                    rows={2} placeholder="Details, features, or offers..." className="w-full px-3 py-2.5 rounded-lg text-sm resize-none"
                    style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.08)', color: '#f5f0e8' }} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-semibold uppercase tracking-wider mb-1 block" style={{ color: 'rgba(245,240,232,0.3)' }}>CTA Button</label>
                    <input data-testid="ai-flyer-cta" value={aiFields.cta_text} onChange={e => setAiFields(f => ({...f, cta_text: e.target.value}))}
                      placeholder="Shop Now" className="w-full px-3 py-2.5 rounded-lg text-sm"
                      style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.08)', color: '#f5f0e8' }} />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold uppercase tracking-wider mb-1 block" style={{ color: 'rgba(245,240,232,0.3)' }}>Brand Name</label>
                    <input data-testid="ai-flyer-brand" value={aiFields.brand_name} onChange={e => setAiFields(f => ({...f, brand_name: e.target.value}))}
                      placeholder="Your Brand" className="w-full px-3 py-2.5 rounded-lg text-sm"
                      style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.08)', color: '#f5f0e8' }} />
                  </div>
                </div>

                {/* Style Selection */}
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-wider mb-2 block" style={{ color: 'rgba(245,240,232,0.3)' }}>Design Style</label>
                  <div className="flex flex-wrap gap-2">
                    {['modern', 'minimal', 'bold', 'luxury', 'playful'].map(s => (
                      <button key={s} onClick={() => setAiStyle(s)} data-testid={`ai-style-${s}`}
                        className="px-3 py-1.5 rounded-lg text-[11px] font-medium capitalize transition-all"
                        style={{
                          background: aiStyle === s ? 'rgba(168,139,250,0.12)' : 'rgba(255,255,255,0.02)',
                          border: `1px solid ${aiStyle === s ? 'rgba(168,139,250,0.3)' : 'rgba(255,255,255,0.06)'}`,
                          color: aiStyle === s ? '#a78bfa' : 'rgba(245,240,232,0.4)',
                        }}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Write this for me for AI mode */}
                <div className="p-3 rounded-xl" style={{ background: 'rgba(200,240,96,0.03)', border: '1px solid rgba(200,240,96,0.08)' }}>
                  <input
                    data-testid="ai-mode-idea-input"
                    value={aiDescription}
                    onChange={e => setAiDescription(e.target.value)}
                    placeholder="Describe your product/brand..."
                    className="w-full px-3 py-2 rounded-lg text-xs mb-2"
                    style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.08)', color: '#f5f0e8' }}
                  />
                  <button
                    data-testid="ai-mode-generate-idea-btn"
                    onClick={async () => {
                      setIdeaLoading(true);
                      try {
                        const { data } = await axios.post(`${API}/api/generate-idea`, {
                          type: 'flyer', product: aiDescription || 'marketing', description: aiDescription,
                        });
                        const idea = data.idea;
                        const f = idea?.fields || idea || {};
                        const allVals = Object.values(f).filter(v => typeof v === 'string');
                        setAiFields({
                          headline: f.headline || f.heading || f.statement || allVals[0] || '',
                          subheadline: f.subheadline || f.sub_headline || f.subtitle || f.offer_text || f.supporting_text || allVals[1] || '',
                          body_text: f.body_text || f.description || f.body || f.story_text || f.quote || allVals[2] || '',
                          cta_text: f.cta_text || f.cta || f.call_to_action || 'Get Started',
                          brand_name: f.brand_name || f.brand || aiDescription?.split(' ')[0] || '',
                        });
                        toast.success('Idea ready. Review and create.');
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to generate idea');
                      }
                      setIdeaLoading(false);
                    }}
                    disabled={ideaLoading}
                    className="w-full py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-2 transition-all"
                    style={{ background: 'rgba(200,240,96,0.08)', color: '#c8f060', border: '1px solid rgba(200,240,96,0.12)' }}>
                    {ideaLoading ? <><ArrowsClockwise size={12} className="animate-spin" /> Writing…</> : <><Sparkle size={12} weight="fill" /> Write this for me (1 credit)</>}
                  </button>
                </div>

                {/* Format Toggle */}
                <div className="flex gap-2">
                  {['square', 'vertical'].map(f => (
                    <button key={f} onClick={() => store.setField('format', f)}
                      className="flex-1 py-2 rounded-lg text-[11px] font-medium capitalize"
                      style={{
                        background: store.format === f ? 'rgba(200,240,96,0.06)' : 'transparent',
                        border: `1px solid ${store.format === f ? 'rgba(200,240,96,0.15)' : 'rgba(255,255,255,0.06)'}`,
                        color: store.format === f ? '#c8f060' : 'rgba(245,240,232,0.3)',
                      }}>
                      {f === 'square' ? '1:1' : '9:16'} {f}
                    </button>
                  ))}
                </div>
              </div>

              <button
                data-testid="generate-ai-flyer-btn"
                onClick={generateAiFlyer}
                disabled={aiGenerating || !aiFields.headline.trim()}
                className="w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all"
                style={{
                  background: aiGenerating ? 'rgba(168,139,250,0.05)' : 'linear-gradient(135deg, rgba(168,139,250,0.15), rgba(200,240,96,0.1))',
                  border: '1px solid rgba(168,139,250,0.2)',
                  color: '#a78bfa',
                  opacity: !aiFields.headline.trim() ? 0.5 : 1,
                }}>
                {aiGenerating ? <ArrowsClockwise size={14} className="animate-spin" /> : <Sparkle size={14} weight="fill" />}
                {aiGenerating ? 'Building Flyer (~30s)...' : 'Build Flyer (3 credits)'}
              </button>
            </div>

            {/* Right: Result Preview */}
            <div className="p-5 rounded-2xl" style={{ background: '#0c0c0c', border: '1px solid rgba(255,255,255,0.06)' }}>
              {store.result?.download_url ? (
                <div>
                  <img src={`${API}${store.result.download_url}`} alt="Flyer" className="w-full rounded-xl" style={{ border: '1px solid rgba(255,255,255,0.06)' }} />
                  <div className="flex gap-2 mt-4">
                    <button onClick={downloadResult} data-testid="download-ai-flyer-btn"
                      className="flex-1 py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-2"
                      style={{ background: 'rgba(200,240,96,0.06)', border: '1px solid rgba(200,240,96,0.15)', color: '#c8f060' }}>
                      <DownloadSimple size={14} /> Download
                    </button>
                    <button onClick={() => shareOrDownload(store.result.download_url, 'image/png', 'Flyer')} data-testid="share-ai-flyer-btn"
                      className="flex-1 py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-2"
                      style={{ background: 'rgba(168,139,250,0.06)', border: '1px solid rgba(168,139,250,0.15)', color: '#a78bfa' }}>
                      <ShareFat size={14} /> Share
                    </button>
                  </div>
                </div>
              ) : aiGenerating ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <ArrowsClockwise size={32} className="animate-spin mb-4" style={{ color: '#a78bfa' }} />
                  <p className="text-sm font-medium" style={{ color: 'rgba(245,240,232,0.5)' }}>Creating your flyer...</p>
                  <p className="text-[11px] mt-1" style={{ color: 'rgba(245,240,232,0.2)' }}>This takes about 30 seconds</p>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'rgba(168,139,250,0.06)' }}>
                    <Sparkle size={24} weight="fill" style={{ color: '#a78bfa' }} />
                  </div>
                  <p className="text-sm font-medium mb-1" style={{ color: 'rgba(245,240,232,0.5)' }}>A complete flyer with graphics will appear here</p>
                  <p className="text-[11px]" style={{ color: 'rgba(245,240,232,0.2)' }}>Fill in the fields and click Generate</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Template Selection */}
        {flyerMode === 'template' && (
        <>
        <div>
          <p className="text-xs font-medium mb-3 uppercase tracking-wider" style={{ color: 'rgba(245,240,232,0.25)' }}>Choose a template</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {templates.map(t => {
              const meta = TEMPLATE_META[t.id] || { icon: '?', color: '#888', description: '', fieldLabels: {}, placeholders: {} };
              return (
                <TemplateCard
                  key={t.id}
                  id={t.id}
                  template={t}
                  meta={meta}
                  selected={store.selectedTemplate}
                  onSelect={selectTemplate}
                />
              );
            })}
          </div>
        </div>

        {/* Editor Panel */}
        <AnimatePresence>
          {store.selectedTemplate && selectedMeta && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-6"
            >
              {/* Left: Fields */}
              <div className="rounded-xl p-6" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-base font-semibold" style={{ color: '#f5f0e8', fontFamily: "'Instrument Serif', serif" }}>
                    {selectedTpl?.name || 'Template'}
                  </h3>
                  <div className="flex gap-2">
                    {['square', 'vertical'].map(f => (
                      <button
                        key={f}
                        data-testid={`format-${f}`}
                        onClick={() => store.setField('format', f)}
                        className="px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all"
                        style={{
                          background: store.format === f ? `${selectedMeta.color}12` : 'transparent',
                          border: `1px solid ${store.format === f ? `${selectedMeta.color}40` : 'rgba(255,255,255,0.06)'}`,
                          color: store.format === f ? selectedMeta.color : 'rgba(245,240,232,0.3)',
                        }}
                      >
                        {f === 'square' ? <><SquaresFour size={12} className="inline mr-1" /> 1080x1080</> : <><Rows size={12} className="inline mr-1" /> 1080x1920</>}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Write this for mes */}
                <div className="mb-5 p-4 rounded-xl" style={{ background: 'rgba(200,240,96,0.03)', border: '1px solid rgba(200,240,96,0.1)' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <Lightning size={14} weight="fill" style={{ color: '#c8f060' }} />
                    <span className="text-xs font-semibold" style={{ color: '#c8f060' }}>Write this for mes</span>
                  </div>
                  <input
                    data-testid="ai-description-input"
                    value={aiDescription}
                    onChange={e => setAiDescription(e.target.value)}
                    placeholder="Describe your product or brand (optional)"
                    className="w-full px-3 py-2 rounded-lg text-xs mb-2"
                    style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.08)', color: '#f5f0e8' }}
                  />
                  <button
                    data-testid="ai-generate-template-btn"
                    onClick={aiGenerateFields}
                    disabled={aiGenerating}
                    className="w-full py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-2 transition-all"
                    style={{
                      background: aiGenerating ? 'rgba(200,240,96,0.06)' : 'rgba(200,240,96,0.1)',
                      color: '#c8f060',
                      border: '1px solid rgba(200,240,96,0.15)',
                    }}
                  >
                    {aiGenerating ? <><ArrowsClockwise size={12} className="animate-spin" /> Writing...</> : <><Sparkle size={12} weight="fill" /> Write Copy</>}
                  </button>
                </div>

                <div className="space-y-4">
                  {Object.entries(selectedMeta.fieldLabels).map(([key, label]) => (
                    <div key={key}>
                      <label className="text-[11px] font-medium uppercase tracking-wider mb-1.5 block" style={{ color: 'rgba(245,240,232,0.3)' }}>{label}</label>
                      {key === 'body_text' || key === 'description' || key === 'quote_text' || key === 'teaser_text' || key === 'before_items' || key === 'after_items' ? (
                        <textarea
                          data-testid={`field-${key}`}
                          rows={3}
                          value={store.fields[key] || ''}
                          onChange={e => handleFieldEdit(key, e.target.value)}
                          placeholder={selectedMeta.placeholders[key]}
                          className="w-full px-3 py-2.5 rounded-lg text-sm resize-none"
                          style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.08)', color: '#f5f0e8' }}
                        />
                      ) : (
                        <input
                          data-testid={`field-${key}`}
                          value={store.fields[key] || ''}
                          onChange={e => handleFieldEdit(key, e.target.value)}
                          placeholder={selectedMeta.placeholders[key]}
                          className="w-full px-3 py-2.5 rounded-lg text-sm"
                          style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.08)', color: '#f5f0e8' }}
                        />
                      )}
                    </div>
                  ))}
                </div>

                {/* AI Background */}
                <div className="mt-4 p-4 rounded-xl" style={{ background: 'rgba(168,139,250,0.04)', border: '1px solid rgba(168,139,250,0.12)' }}>
                  <span className="text-[11px] font-semibold uppercase tracking-wider block mb-3" style={{ color: '#a78bfa' }}>AI Background</span>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {['corporate','creative','nature','tech','luxury','minimalist','gradient','urban'].map(s => (
                      <button key={s} onClick={() => setBgStyle(s)}
                        className="px-2.5 py-1 rounded text-[10px] font-medium capitalize transition-all"
                        style={{
                          background: bgStyle === s ? 'rgba(168,139,250,0.15)' : 'rgba(255,255,255,0.03)',
                          border: `1px solid ${bgStyle === s ? 'rgba(168,139,250,0.3)' : 'rgba(255,255,255,0.06)'}`,
                          color: bgStyle === s ? '#a78bfa' : 'rgba(245,240,232,0.4)',
                        }}>
                        {s}
                      </button>
                    ))}
                  </div>
                  <button onClick={generateBackground} disabled={bgGenerating} data-testid="generate-ai-bg-btn"
                    className="w-full py-2.5 rounded-lg text-xs font-medium flex items-center justify-center gap-2 transition-all"
                    style={{
                      background: bgGenerating ? 'rgba(168,139,250,0.06)' : 'rgba(168,139,250,0.1)',
                      border: '1px solid rgba(168,139,250,0.2)',
                      color: '#a78bfa',
                      opacity: bgGenerating ? 0.6 : 1,
                    }}>
                    {bgGenerating ? <ArrowsClockwise size={12} className="animate-spin" /> : <Sparkle size={12} weight="fill" />}
                    {bgGenerating ? 'Generating background...' : 'Generate AI Background'}
                  </button>
                  {store.fields.background_image && (
                    <div className="mt-2 flex items-center justify-between">
                      <p className="text-[10px]" style={{ color: 'rgba(200,240,96,0.5)' }}>Background ready — click Render</p>
                      <button onClick={() => store.updateField('background_image', '')} className="text-[10px]" style={{ color: 'rgba(245,240,232,0.3)' }}>Remove</button>
                    </div>
                  )}
                </div>

                {/* Render Button */}
                <button
                  data-testid="render-template-btn"
                  onClick={renderTemplate}
                  disabled={store.rendering}
                  className="w-full mt-6 py-3.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all"
                  style={{
                    background: store.rendering ? 'rgba(200,240,96,0.06)' : '#c8f060',
                    color: store.rendering ? '#c8f060' : '#080808',
                    opacity: store.rendering ? 0.7 : 1,
                  }}
                >
                  {store.rendering ? (
                    <><ArrowsClockwise size={16} className="animate-spin" /> Rendering...</>
                  ) : (
                    <><Image size={16} /> Render PNG (1 credit)</>
                  )}
                </button>
              </div>

              {/* Right: Preview / Result */}
              <div className="rounded-xl p-6" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
                <h3 className="text-base font-semibold mb-4" style={{ color: '#f5f0e8', fontFamily: "'Instrument Serif', serif" }}>Preview</h3>

                {store.rendering && (
                  <div className="flex flex-col items-center justify-center py-16">
                    <ArrowsClockwise size={32} className="animate-spin mb-4" style={{ color: selectedMeta.color }} />
                    <p className="text-sm" style={{ color: 'rgba(245,240,232,0.5)' }}>Rendering high-resolution PNG...</p>
                    <p className="text-[11px] mt-1" style={{ color: 'rgba(245,240,232,0.2)' }}>This takes 3-5 seconds</p>
                  </div>
                )}

                {store.result && !store.rendering && (
                  <div>
                    <div className="rounded-lg overflow-hidden mb-3 relative" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
                      <img
                        data-testid="rendered-preview"
                        src={`${API}${store.result.download_url}`}
                        alt="Rendered template"
                        className="w-full"
                        style={{ maxHeight: '500px', objectFit: 'contain', background: '#0a0a0a' }}
                      />
                    </div>

                    {/* Edit Controls */}
                    <div className="mb-3 p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'rgba(200,240,96,0.5)' }}>Edit & Re-render</span>
                        <button onClick={renderTemplate} data-testid="re-render-flyer-btn"
                          className="px-3 py-1 rounded text-[10px] font-semibold flex items-center gap-1.5"
                          style={{ background: '#c8f060', color: '#080808' }}>
                          <ArrowsClockwise size={10} /> Re-render
                        </button>
                      </div>
                      <div className="space-y-2">
                        {selectedMeta && Object.keys(selectedMeta.fieldLabels || {}).map(fk => (
                          <div key={fk}>
                            <label className="text-[9px] uppercase tracking-wider block mb-0.5" style={{ color: 'rgba(245,240,232,0.25)' }}>
                              {selectedMeta.fieldLabels[fk] || fk.replace(/_/g, ' ')}
                            </label>
                            {(fk.includes('body') || fk.includes('description') || fk.includes('text') || fk.includes('story') || fk.includes('testimonial') || fk.includes('quote')) ? (
                              <textarea
                                value={store.fields[fk] || ''}
                                onChange={e => store.updateField(fk, e.target.value)}
                                rows={2}
                                className="w-full px-2 py-1.5 rounded text-[11px] resize-none"
                                style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.08)', color: '#f5f0e8' }}
                              />
                            ) : (
                              <input
                                value={store.fields[fk] || ''}
                                onChange={e => store.updateField(fk, e.target.value)}
                                className="w-full px-2 py-1.5 rounded text-[11px]"
                                style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.08)', color: '#f5f0e8' }}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                      <p className="text-[9px] mt-2" style={{ color: 'rgba(245,240,232,0.2)' }}>Edit any field above and click Re-render to apply</p>
                    </div>

                    <div className="flex gap-2">
                      <button
                        data-testid="download-rendered-btn"
                        onClick={downloadResult}
                        className="flex-1 py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
                        style={{ background: '#c8f060', color: '#080808' }}
                      >
                        <DownloadSimple size={16} /> Download
                      </button>
                      <button
                        data-testid="share-flyer-btn"
                        onClick={() => shareOrDownload(
                          store.result.download_url, 'image/png', 'Flyer'
                        )}
                        className="px-4 py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-2"
                        style={{ background: 'rgba(168,139,250,0.08)', border: '1px solid rgba(168,139,250,0.2)', color: '#a78bfa' }}
                      >
                        <ShareFat size={16} /> Share
                      </button>
                      <button
                        onClick={() => { store.setField('result', null); setTimeout(() => fetchPreviewHtml(), 100); }}
                        className="px-4 py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-2"
                        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(245,240,232,0.5)' }}
                      >
                        <PencilSimple size={14} /> Edit
                      </button>
                    </div>
                    <p className="text-[11px] text-center mt-3" style={{ color: 'rgba(245,240,232,0.2)' }}>
                      {store.result.width}x{store.result.height}px &middot; {store.result.credits_used} credit used &middot; Edit fields above and Re-render
                    </p>
                  </div>
                )}

                {!store.result && !store.rendering && (
                  <div data-testid="flyer-live-editor">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <CursorClick size={12} weight="fill" style={{ color: '#c8f060' }} />
                        <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'rgba(200,240,96,0.6)' }}>
                          Live Preview — Click text to edit
                        </span>
                      </div>
                      <button
                        onClick={fetchPreviewHtml}
                        data-testid="refresh-preview-btn"
                        className="flex items-center gap-1 px-2 py-1 rounded text-[10px] transition-all hover:bg-white/5"
                        style={{ color: 'rgba(245,240,232,0.3)' }}
                      >
                        <ArrowsClockwise size={10} /> Refresh
                      </button>
                    </div>
                    <div
                      ref={previewContainerRef}
                      style={{
                        width: '100%',
                        overflow: 'hidden',
                        borderRadius: '12px',
                        border: '1px solid rgba(255,255,255,0.08)',
                        background: '#0a0a0a',
                        position: 'relative',
                        paddingBottom: store.format === 'vertical' ? '177.78%' : '100%',
                      }}
                    >
                      <iframe
                        ref={iframeRef}
                        data-testid="flyer-live-preview-iframe"
                        title="Flyer Live Preview"
                        sandbox="allow-scripts allow-same-origin"
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '1080px',
                          height: store.format === 'vertical' ? '1920px' : '1080px',
                          transform: `scale(${previewScale})`,
                          transformOrigin: 'top left',
                          border: 'none',
                          display: 'block',
                        }}
                      />
                    </div>
                    <p className="text-[9px] text-center mt-2" style={{ color: 'rgba(245,240,232,0.25)' }}>
                      Click directly on any text to edit it. Changes sync to the fields panel.
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* End Template Mode */}
        </>
        )}

        {/* History */}
        <AnimatePresence>
          {showHistory && store.history.length > 0 && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
              <div className="rounded-xl p-5" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
                <h3 className="text-base font-semibold mb-4" style={{ color: '#f5f0e8', fontFamily: "'Instrument Serif', serif" }}>Recent Renders</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {store.history.map(r => (
                    <div key={r.render_id} className="p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                      <div className="rounded-md overflow-hidden mb-2" style={{ background: '#0a0a0a' }}>
                        <img
                          src={`${API}/api/media/download/${r.render_id}`}
                          alt={r.template_name}
                          className="w-full h-32 object-cover"
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                      </div>
                      <p className="text-xs font-medium truncate" style={{ color: '#f5f0e8' }}>{r.template_name}</p>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-[10px]" style={{ color: 'rgba(245,240,232,0.2)' }}>
                          {r.format} &middot; {r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}
                        </p>
                        <div className="flex gap-1">
                          <button onClick={() => window.open(`${API}/api/media/download/${r.render_id}`, '_blank')} className="p-1 rounded hover:bg-white/5">
                            <DownloadSimple size={12} style={{ color: 'rgba(245,240,232,0.3)' }} />
                          </button>
                          <button onClick={() => deleteRender(r.render_id)} className="p-1 rounded hover:bg-red-500/10">
                            <Trash size={12} style={{ color: 'rgba(255,68,68,0.4)' }} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DashboardLayout>
  );
}
